import React, { useState, useEffect } from 'react';
import { settingsAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Clock, Loader2, Send, Eye, CalendarDays, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { fehlertext } from '../lib/fehlertext';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';

var STUFEN = [
  { wert: 'vorwarnung', name: 'Erste Erinnerung (Vorwarnung)' },
  { wert: 'letzter_tag', name: 'Zweite Erinnerung (letzter Arbeitstag)' },
  { wert: 'dringend', name: 'Dritte Erinnerung (Mahnung)' },
];

export function ReminderSettings() {
  var [data, setData] = useState(null);
  var [saving, setSaving] = useState(false);
  var [testing, setTesting] = useState(false);
  var [testResult, setTestResult] = useState(null);
  var [vorschauDaten, setVorschauDaten] = useState(null);
  var [bestaetigen, setBestaetigen] = useState(false);
  var [stufe, setStufe] = useState('vorwarnung');
  var [warnungen, setWarnungen] = useState([]);
  var [tage, setTage] = useState(null);

  useEffect(function() {
    settingsAPI.getReminders().then(function(res) { setData(res.data); }).catch(function() {});
  }, []);

  function save() {
    setSaving(true);
    var v = data.vorlagen || {};
    var nutzlast = {
      enabled: data.enabled, tage_vorher: data.tage_vorher, second_day: data.second_day,
    };
    STUFEN.forEach(function(s) {
      nutzlast['subject_' + s.wert] = (v[s.wert] || {}).subject || '';
      nutzlast['body_' + s.wert] = (v[s.wert] || {}).body_template || '';
    });
    settingsAPI.updateReminders(nutzlast).then(function(res) {
      toast.success('Erinnerungseinstellungen gespeichert');
      setWarnungen((res.data && res.data.warnungen) || []);
      setTage(null);
    }).catch(function(err) {
      toast.error(fehlertext(err, 'Fehler beim Speichern'));
    }).finally(function() { setSaving(false); });
  }

  // Vorschau: Trockenlauf, verschickt nichts
  function vorschau() {
    setTesting(true); setTestResult(null); setVorschauDaten(null);
    settingsAPI.runRemindersNow(stufe, true).then(function(res) {
      var d = res.data;
      if (d.skipped) { setTestResult('Übersprungen: ' + d.skipped); return; }
      setVorschauDaten(d);
      var zuVersenden = (d.anzahl || 0) - (d.uebersprungen || 0);
      setTestResult(zuVersenden + ' Mitarbeiter würden eine Erinnerung erhalten (Monat '
        + d.month + '/' + d.year + ', Frist ' + (d.frist || '-') + '), '
        + (d.uebersprungen || 0) + ' Testadressen übersprungen.');
    }).catch(function(err) {
      setTestResult(fehlertext(err, 'Vorschau nicht möglich'));
    }).finally(function() { setTesting(false); });
  }

  // Echtversand: nur mit ausdrücklicher Bestätigung
  function wirklichVersenden() {
    setBestaetigen(false);
    setTesting(true); setTestResult(null);
    settingsAPI.runRemindersNow(stufe, false, 'SENDEN').then(function(res) {
      var d = res.data;
      if (d.skipped) { setTestResult('Übersprungen: ' + d.skipped); }
      else {
        setTestResult((d.sent || 0) + ' Erinnerungen versendet, ' + (d.failed || 0)
          + ' fehlgeschlagen, ' + (d.uebersprungen || 0) + ' übersprungen (Monat '
          + d.month + '/' + d.year + ')');
      }
    }).catch(function(err) {
      setTestResult(fehlertext(err, 'Versand nicht möglich'));
    }).finally(function() { setTesting(false); });
  }

  function ausloesetageLaden() {
    settingsAPI.getReminderAusloesetage().then(function(res) {
      setTage(res.data);
      setWarnungen((res.data && res.data.warnungen) || []);
    }).catch(function(err) { toast.error(fehlertext(err, 'Auslösetage nicht abrufbar')); });
  }

  if (!data) return null;

  function upd(key, val) { setData(Object.assign({}, data, { [key]: val })); }

  function updVorlage(s, feld, val) {
    var v = Object.assign({}, data.vorlagen);
    v[s] = Object.assign({}, v[s], { [feld]: val });
    setData(Object.assign({}, data, { vorlagen: v }));
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg"><Clock className="h-5 w-5" /> Erinnerungen</CardTitle>
        <CardDescription>Drei automatische E-Mail-Erinnerungen bei fehlenden Stundenzetteln</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between" data-testid="reminder-enabled-switch">
          <div>
            <Label className="text-sm font-medium">Erinnerungen aktiviert</Label>
            <p className="text-xs text-slate-500">Täglich um 08:00 Uhr wird geprüft, ob Erinnerungen fällig sind</p>
          </div>
          <Switch checked={data.enabled} onCheckedChange={function(v) { upd('enabled', v); }} />
        </div>

        {data.migration_hinweis && (
          <div className="flex gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800"
               data-testid="reminder-migration-hinweis">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            <span>Ihr früherer Erinnerungstext wurde nicht übernommen: er enthält weder
              {' {frist}'} noch {'{tage_uebrig}'} und hätte die Erinnerung ohne Countdown
              verschickt. Es gilt der neue Vorgabetext ({data.migration_hinweis}).</span>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Tage vor dem letzten Arbeitstag</Label>
            <Input type="number" min={1} max={15} value={data.tage_vorher}
              data-testid="reminder-tage-vorher"
              onChange={function(e) { upd('tage_vorher', parseInt(e.target.value) || 5); }} />
            <p className="text-xs text-slate-400 mt-1">
              Neu: kein fester Tag im Monat mehr. Bei 5 geht die erste Erinnerung fünf
              Kalendertage vor dem letzten Arbeitstag raus (September 2026: am 25.09.,
              Frist 30.09.). Wochenenden zählen mit, gesetzliche Feiertage nicht.
            </p>
          </div>
          <div>
            <Label className="text-xs">Dritte Erinnerung (Tag im Folgemonat)</Label>
            <Input type="number" min={1} max={28} value={data.second_day}
              data-testid="reminder-second-day"
              onChange={function(e) { upd('second_day', parseInt(e.target.value) || 1); }} />
            <p className="text-xs text-slate-400 mt-1">
              Die Mahnung an alle, deren Stundenzettel dann noch fehlt.
            </p>
          </div>
        </div>

        {warnungen.length > 0 && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-1"
               data-testid="reminder-warnungen">
            {warnungen.map(function(w, i) {
              return <p key={i} className="text-xs text-amber-800">{w}</p>;
            })}
          </div>
        )}

        {STUFEN.map(function(s) {
          var v = (data.vorlagen || {})[s.wert] || {};
          return (
            <div key={s.wert} className="rounded-lg border p-3 space-y-2" data-testid={'reminder-vorlage-' + s.wert}>
              <p className="text-sm font-medium text-slate-700">{s.name}</p>
              <div>
                <Label className="text-xs">Betreff</Label>
                <Input value={v.subject || ''} data-testid={'reminder-subject-' + s.wert}
                  onChange={function(e) { updVorlage(s.wert, 'subject', e.target.value); }} />
              </div>
              <div>
                <Label className="text-xs">E-Mail-Vorlage</Label>
                <Textarea rows={5} value={v.body_template || ''} data-testid={'reminder-body-' + s.wert}
                  onChange={function(e) { updVorlage(s.wert, 'body_template', e.target.value); }} />
              </div>
            </div>
          );
        })}

        <p className="text-xs text-slate-400">
          Platzhalter: {'{name}'}, {'{month}'}, {'{year}'}, {'{link}'}, {'{frist}'} (letzter
          Arbeitstag, z. B. 30.09.), {'{tage_uebrig}'}. Achtung: {'{tage_uebrig}'} enthält das
          Wort bereits — schreiben Sie „noch {'{tage_uebrig}'}.", nicht „noch {'{tage_uebrig}'} Tage.".
          Für die dritte Erinnerung ergibt {'{tage_uebrig}'} immer „0 Tage".
        </p>

        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={save} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}Speichern
          </Button>
          <Button variant="outline" onClick={ausloesetageLaden} data-testid="reminder-ausloesetage-btn">
            <CalendarDays className="h-4 w-4 mr-1" />Auslösetage anzeigen
          </Button>
        </div>

        {tage && (
          <div className="border rounded-lg p-3 bg-slate-50 space-y-1" data-testid="reminder-ausloesetage-box">
            <p className="text-xs font-medium text-slate-600">
              Auslösetage (Feiertage {tage.bundesland}, {tage.tage_vorher} Tage vorher)
            </p>
            {(tage.monate || []).map(function(m) {
              return (
                <p key={m.bezugsmonat} className="text-xs text-slate-600">
                  Bezugsmonat {m.bezugsmonat}: {m.vorwarnung}, {m.letzter_tag}, {m.dringend}
                  {m.dringend_moeglich ? '' : ' (dritte Erinnerung entfällt in diesem Monat)'}
                </p>
              );
            })}
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap border-t pt-3">
          <span className="text-xs text-slate-500">Stufe zum Prüfen:</span>
          {STUFEN.map(function(s) {
            return (
              <Button key={s.wert} size="sm" variant={stufe === s.wert ? 'default' : 'outline'}
                      onClick={function() { setStufe(s.wert); setVorschauDaten(null); setTestResult(null); }}
                      data-testid={'reminder-stufe-' + s.wert}>
                {s.name}
              </Button>
            );
          })}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={vorschau} disabled={testing} data-testid="reminder-preview-btn">
            {testing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Eye className="h-4 w-4 mr-1" />}
            Vorschau
          </Button>
          <Button variant="outline" onClick={function() { setBestaetigen(true); }} disabled={testing}
                  className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="reminder-send-btn">
            <Send className="h-4 w-4 mr-1" />
            Jetzt wirklich versenden
          </Button>
          {testResult && <span className="text-sm text-slate-600" data-testid="reminder-result">{testResult}</span>}
        </div>

        {vorschauDaten && vorschauDaten.beispiel && vorschauDaten.beispiel.subject && (
          <div className="border rounded-lg p-3 bg-slate-50 space-y-1" data-testid="reminder-preview-box">
            <p className="text-xs font-medium text-slate-600">Beispieltext (nichts versendet)</p>
            <p className="text-sm font-medium text-slate-800">{vorschauDaten.beispiel.subject}</p>
            <pre className="text-xs text-slate-600 whitespace-pre-wrap font-sans">{vorschauDaten.beispiel.body}</pre>
          </div>
        )}

        <AlertDialog open={bestaetigen} onOpenChange={setBestaetigen}>
          <AlertDialogContent data-testid="reminder-confirm-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Erinnerungen jetzt wirklich versenden?</AlertDialogTitle>
              <AlertDialogDescription>
                {vorschauDaten
                  ? 'Es gehen ' + ((vorschauDaten.anzahl || 0) - (vorschauDaten.uebersprungen || 0))
                    + ' echte E-Mails an Mitarbeiter raus.'
                  : 'Es gehen echte E-Mails an alle Mitarbeiter mit offenem Stundenzettel raus. Bitte vorher die Vorschau benutzen.'}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="reminder-confirm-cancel">Abbrechen</AlertDialogCancel>
              <AlertDialogAction onClick={wirklichVersenden} data-testid="reminder-confirm-ok">Ja, versenden</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
